import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { slug } from 'src/utils/deburr';
import { SelectionNewVersion } from 'src/domains/sportsbook/webview/components/Selection';
import { EventSummaryConnect } from 'src/domains/sportsbook/webview/components/containers/eventSummary/EventSummary';
import {
    EventRowMeta,
    EventRowNameLink,
    EventRowWrapper,
} from 'src/domains/sportsbook/webview/components/eventRow/EventRow.style';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import {
    EventRowHeader,
    EventRowMarkets,
    EventRowSelections,
    EventRowSelectionsElement,
    EventRowMetaTime,
} from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowMobx.style';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { getMarketForDisplay } from 'src/domains/sportsbook/webview/components/sportEventsListNew/getEventAndMarket';
import { SingleScore } from 'src/domains/sportsbook/webview/components/events/row/Score';
import { Participant } from 'src/domains/sportsbook/webview/components/events/row/Participant';
import { EventStatusAndDate } from 'src/domains/sportsbook/webview/components/events/row/StatusAndDate';
import { AppState, useAppStateContext } from 'src/appState/AppState';
import { UnifiedSportEventsListState } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedSportEventsListState';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { getLastSelections } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedEventsListMarkets';

const getMarket = (
    appState: AppState,
    smallEvent: EventListGroupEventItemType,
    eventsListState: UnifiedSportEventsListState
): MarketModel | null => {
    const eventModel = smallEvent.id2.getEventModel();

    if (eventModel === null) {
        return null;
    }

    const marketModel = getMarketForDisplay(appState, eventModel, eventsListState);

    if (marketModel === null || marketModel === 'loading') {
        return null;
    }

    return marketModel;
};

interface EventRowUnifiedRenderSelectionsPropsType {
    smallEvent: EventListGroupEventItemType;
    eventsListState: UnifiedSportEventsListState;
    selectionsHeaders: Array<string>;
}

const EventRowUnifiedRenderSelections = observer(
    'EventRowUnifiedRenderSelections',
    (props: EventRowUnifiedRenderSelectionsPropsType): JSX.Element | null => {
        const { smallEvent, eventsListState, selectionsHeaders } = props;
        const appState = useAppStateContext();
        const common = useCommon();
        const configState = ConfigComponents.get(common).config;
        const { eventRowVariant } = configState.componentVariants;

        const market = getMarket(appState, smallEvent, eventsListState);

        if (market === null) {
            return null;
        }

        const selections = selectionsHeaders.length > 0 ? selectionsHeaders : getLastSelections(market.template.id);

        return selections.length > 0 ? (
            <EventRowSelections variant={eventRowVariant}>
                {selections.map((id) => {
                    const selection = market.selections.find((s) => {
                        const selectionIdentifier = s.selectionIdentifiers;

                        return market.displayed && s.display && selectionIdentifier === id;
                    });

                    return (
                        <EventRowSelectionsElement key={id}>
                            <SelectionNewVersion
                                selectionId={selection?.id2}
                                sp={false}
                                borderLeft={true}
                                borderRight={true}
                            />
                        </EventRowSelectionsElement>
                    );
                })}
            </EventRowSelections>
        ) : null;
    }
);

interface EventRowUnifiedPropsType {
    smallEvent: EventListGroupEventItemType;
    competitionId: CompetitionId | null;
    sportId: string;
    selectionsHeaders: Array<string>;
}

const EventRowUnified = observer('EventRowUnified', (props: EventRowUnifiedPropsType) => {
    const { smallEvent, competitionId, sportId, selectionsHeaders } = props;

    const { appSportsBookState } = useAppStateContext();
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const { eventRowVariant } = configState.componentVariants;
    const eventsListState = appSportsBookState.unifiedSportEventsListState.get(sportId).get(competitionId);
    const isOutrights = smallEvent.template === 'outrights';

    const filterMarketsByPopularTag = false;

    return (
        <EventRowWrapper
            variant={eventRowVariant}
            isOutrights={isOutrights}
            data-testid='event-row-unified'
        >
            <EventRowHeader variant={eventRowVariant}>
                <EventRowNameLink
                    to={{ name: 'event', id: smallEvent.id, slug: slug(smallEvent.name) }}
                    dataTest='EventRowUnifiedNameLink'
                    variant={eventRowVariant}
                >
                    <Participant smallEvent={smallEvent} />

                    <EventRowMeta>
                        {isOutrights ? (
                            <EventStatusAndDate
                                dateFormat='onlyToday'
                                smallEvent={smallEvent}
                            />
                        ) : (
                            <EventRowMetaTime>
                                <EventStatusAndDate
                                    dateFormat='onlyToday'
                                    smallEvent={smallEvent}
                                />
                            </EventRowMetaTime>
                        )}
                    </EventRowMeta>
                </EventRowNameLink>

                <SingleScore smallEvent={smallEvent} />
            </EventRowHeader>

            {isOutrights ? (
                <EventRowMarkets>
                    <EventSummaryConnect
                        id={appSportsBookState.models.id.getEventId(smallEvent.id)}
                        filterMarketsByPopularTag={filterMarketsByPopularTag}
                    />
                </EventRowMarkets>
            ) : (
                <EventRowUnifiedRenderSelections
                    smallEvent={smallEvent}
                    eventsListState={eventsListState}
                    selectionsHeaders={selectionsHeaders}
                />
            )}
        </EventRowWrapper>
    );
});

interface EventRowUnifiedWrapperPropsType {
    smallEvent: EventListGroupEventItemType;
    competitionId: CompetitionId | null;
    sportId: string;
    selectionsHeaders: Array<string>;
}

export const EventRowUnifiedWrapper = observer('EventRowUnifiedWrapper', (props: EventRowUnifiedWrapperPropsType) => {
    const { smallEvent, competitionId, sportId, selectionsHeaders } = props;

    return (
        <EventRowUnified
            smallEvent={smallEvent}
            competitionId={competitionId}
            sportId={sportId}
            selectionsHeaders={selectionsHeaders}
        />
    );
});
